import { Box } from '@mui/material';
import type { ReactNode } from 'react';
import type { SeriesLineStyle } from '../../model/types';
import { SeriesChartType, SeriesPercentDiff } from '../../model/types';
import { LINE_STYLE_DASH_ARRAY } from '../SeriesChart/constants';

const PREVIEW_WIDTH = 56;
const PREVIEW_HEIGHT = 22;
const STROKE = '#343844';

interface SvgProps {
  children: ReactNode;
}

function PreviewSvg({ children }: SvgProps) {
  return (
    <svg
      width={PREVIEW_WIDTH}
      height={PREVIEW_HEIGHT}
      viewBox={`0 0 ${PREVIEW_WIDTH} ${PREVIEW_HEIGHT}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {children}
    </svg>
  );
}

export function OptionLabel({
  preview,
  caption,
}: {
  preview: ReactNode;
  caption: string;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        py: 0.5,
      }}
    >
      {preview}
      <Box component="span" sx={{ fontSize: '0.75rem' }}>
        {caption}
      </Box>
    </Box>
  );
}

const CHART_TYPE_PREVIEW: Record<SeriesChartType, ReactNode> = {
  [SeriesChartType.line]: (
    <PreviewSvg>
      <path
        d="M4 16L17 8L30 13L42 4L52 9"
        stroke={STROKE}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </PreviewSvg>
  ),
  [SeriesChartType.step]: (
    <PreviewSvg>
      <path
        d="M4 17H16V9H28V13H40V5H52"
        stroke={STROKE}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </PreviewSvg>
  ),
  [SeriesChartType.area]: (
    <PreviewSvg>
      <path
        d="M4 16L17 8L30 13L42 4L52 9V19H4V16Z"
        fill={STROKE}
        opacity="0.2"
      />
      <path
        d="M4 16L17 8L30 13L42 4L52 9"
        stroke={STROKE}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </PreviewSvg>
  ),
  [SeriesChartType.bar]: (
    <PreviewSvg>
      <rect x="7" y="13" width="7" height="6" rx="1" fill={STROKE} />
      <rect x="19" y="6" width="7" height="13" rx="1" fill={STROKE} />
      <rect x="31" y="10" width="7" height="9" rx="1" fill={STROKE} />
      <rect x="43" y="3" width="7" height="16" rx="1" fill={STROKE} />
    </PreviewSvg>
  ),
};

export const getChartTypePreview = (type: SeriesChartType): ReactNode => {
  return CHART_TYPE_PREVIEW[type];
};

export const getLineWidthPreview = (width: number): ReactNode => {
  return (
    <PreviewSvg>
      <path
        d={`M4 ${PREVIEW_HEIGHT / 2}H52`}
        stroke={STROKE}
        strokeWidth={width}
        strokeLinecap="round"
      />
    </PreviewSvg>
  );
};

export const byMetricPreview: ReactNode = (
  <PreviewSvg>
    <path d="M4 7H52" stroke={STROKE} strokeWidth="1.5" strokeLinecap="round" />
    <path
      d="M4 16H52"
      stroke={STROKE}
      strokeWidth="4.5"
      strokeLinecap="round"
    />
  </PreviewSvg>
);

export const getLineStylePreview = (style: SeriesLineStyle): ReactNode => {
  return (
    <PreviewSvg>
      <path
        d={`M4 ${PREVIEW_HEIGHT / 2}H52`}
        stroke={STROKE}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray={LINE_STYLE_DASH_ARRAY[style]}
      />
    </PreviewSvg>
  );
};

const sparkline = (
  <path
    d="M6 16L18 11L30 14L42 6"
    stroke={STROKE}
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  />
);

const dot = (cx: number, cy: number) => {
  return <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="2" fill={STROKE} />;
};

const percentMark = (x: number, y: number) => {
  return (
    <text
      key={`${x}-${y}`}
      x={x}
      y={y}
      fontSize="8"
      fill={STROKE}
      textAnchor="middle"
    >
      %
    </text>
  );
};

export const valueLabelsPreview = {
  none: <PreviewSvg>{sparkline}</PreviewSvg>,
  all: (
    <PreviewSvg>
      {sparkline}
      {[
        [6, 16],
        [18, 11],
        [30, 14],
        [42, 6],
      ].map(([cx, cy]) => dot(cx, cy))}
    </PreviewSvg>
  ),
  startEnd: (
    <PreviewSvg>
      {sparkline}
      {dot(6, 16)}
      {dot(42, 6)}
    </PreviewSvg>
  ),
};

export const percentDiffPreview: Record<SeriesPercentDiff, ReactNode> = {
  [SeriesPercentDiff.none]: <PreviewSvg>{sparkline}</PreviewSvg>,
  [SeriesPercentDiff.fromFirst]: (
    <PreviewSvg>
      <path
        d="M6 16H50"
        stroke={STROKE}
        strokeWidth="1"
        strokeDasharray="3 3"
        opacity="0.5"
      />
      {sparkline}
      {percentMark(48, 11)}
    </PreviewSvg>
  ),
  [SeriesPercentDiff.fromPrevious]: (
    <PreviewSvg>
      {sparkline}
      {percentMark(18, 7)}
      {percentMark(30, 10)}
    </PreviewSvg>
  ),
};
